import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FlaskConical,
  Columns,
  Sparkles,
  CheckCircle2,
  RefreshCw,
  GitBranch,
  ArrowLeft,
  ChevronDown,
  Moon,
  Sun,
  MessageSquare,
  Search,
  TrendingUp,
  ShieldCheck,
  ExternalLink,
} from "lucide-react";
import { SocialTab } from "../components/social/SocialTab";
import { SocialTabSandbox } from "../components/social/sandbox/SocialTabSandbox";
import { LoadingState } from "../components/LoadingState";
import { useDashboard } from "../context/DashboardContext";
import { fetchClientDetail } from "../api";
import type { SocialDetailPayload, GertrudisAuditPayload } from "../types";
import "./sandbox.css";

type ViewMode = "sandbox" | "compare" | "official";
type LabTab = "social" | "seo" | "paid";

export function SandboxPage() {
  const { data: dashboardData, selectedClient, setSelectedClient, loading: dashboardLoading } = useDashboard();
  const [viewMode, setViewMode] = useState<ViewMode>("sandbox");
  const [activeTab, setActiveTab] = useState<LabTab>("social");
  const [socialData, setSocialData] = useState<SocialDetailPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Theme support local al Sandbox
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    try {
      const stored = window.localStorage.getItem("wolf.theme");
      if (stored === "light" || stored === "dark") return stored;
    } catch {
      /* fallback */
    }
    return document.body.classList.contains("light-mode") ? "light" : "dark";
  });

  useEffect(() => {
    if (theme === "light") {
      document.body.classList.add("light-mode");
      document.documentElement.setAttribute("data-theme", "light");
    } else {
      document.body.classList.remove("light-mode");
      document.documentElement.setAttribute("data-theme", "dark");
    }
    try {
      window.localStorage.setItem("wolf.theme", theme);
    } catch {
      /* fallback */
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((curr) => (curr === "light" ? "dark" : "light"));
  };

  // Cliente efectivo: si está en ALL, usa el primero disponible o "chp"
  const effectiveClient = useMemo(() => {
    if (selectedClient && selectedClient !== "ALL") return selectedClient;
    return dashboardData?.clients?.[0]?.clientSlug || "chp";
  }, [selectedClient, dashboardData]);

  const loadData = () => {
    if (!effectiveClient) return;
    setLoading(true);
    setError(null);
    fetchClientDetail(effectiveClient, "social")
      .then((res) => {
        setSocialData(res as SocialDetailPayload);
      })
      .catch((err) => {
        console.error("Error cargando datos de Social en Sandbox:", err);
        setError(err instanceof Error ? err.message : "Error al cargar datos.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveClient]);

  const handleAuditGenerated = (audit: GertrudisAuditPayload) => {
    setSocialData((curr) => {
      if (!curr) return curr;
      const history = curr.audit?.history ? [audit, ...curr.audit.history] : [audit];
      return { ...curr, audit: { ...audit, history } };
    });
  };

  const clientName =
    dashboardData?.clients.find((c) => c.clientSlug === effectiveClient)?.displayName ||
    effectiveClient.toUpperCase();

  return (
    <div className="sandbox-standalone">
      {/* 1. Dedicated Sandbox Header */}
      <header className="sandbox-topbar">
        <div className="sandbox-brand-group">
          <div className="sandbox-logo-badge" title="Wolf Live Lab Environment">
            <FlaskConical size={20} />
          </div>

          <div className="sandbox-brand-text">
            <div className="sandbox-title-row">
              <span className="sandbox-main-title">Wolf Live Lab</span>
              <span className="sandbox-env-badge">
                <span className="pulse-dot" /> Ambiente Aislado
              </span>
            </div>
            <nav className="sandbox-breadcrumb" aria-label="Breadcrumb">
              <Link to={`/cliente${effectiveClient ? `?client=${effectiveClient}` : ""}`}>
                <ArrowLeft size={12} /> Dashboard
              </Link>
              <span className="separator">/</span>
              <span>Sandbox</span>
              <span className="separator">/</span>
              <span className="active-crumb">
                {activeTab === "social" ? "Laboratorio Social" : activeTab.toUpperCase()}
              </span>
            </nav>
          </div>
        </div>

        {/* Controles de cabecera */}
        <div className="sandbox-topbar-controls">
          {dashboardData?.clients && (
            <div className="sandbox-client-select-wrap">
              <select
                aria-label="Seleccionar cliente en Sandbox"
                value={effectiveClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="sandbox-client-select"
              >
                {dashboardData.clients.map((c) => (
                  <option key={c.clientSlug} value={c.clientSlug}>
                    {c.displayName}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="sandbox-client-select-icon" />
            </div>
          )}

          <button
            type="button"
            onClick={toggleTheme}
            className="sandbox-btn sandbox-btn-icon-only"
            title={theme === "light" ? "Cambiar a modo oscuro" : "Cambiar a modo claro"}
            aria-label="Toggle tema claro/oscuro"
          >
            {theme === "light" ? <Moon size={15} /> : <Sun size={15} />}
          </button>

          <button
            type="button"
            onClick={loadData}
            disabled={loading}
            className="sandbox-btn sandbox-btn-icon-only"
            title="Recargar datos del cliente"
            aria-label="Recargar datos"
          >
            <RefreshCw size={15} className={loading ? "spin-animation" : ""} />
          </button>

          <Link
            to={`/cliente${effectiveClient ? `?client=${effectiveClient}` : ""}`}
            className="sandbox-btn sandbox-btn-return"
            title="Volver a la vista oficial de producción"
          >
            <ExternalLink size={13} />
            <span>Dashboard Oficial</span>
          </Link>
        </div>
      </header>

      {/* 2. Context Navigation Strip */}
      <div className="sandbox-nav-strip">
        <div className="sandbox-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "social"}
            className={`sandbox-tab-item ${activeTab === "social" ? "is-active" : ""}`}
            onClick={() => setActiveTab("social")}
          >
            <MessageSquare size={15} />
            <span>Social Feedback Lab</span>
            <span className="tab-chip">MCP Live</span>
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={false}
            disabled
            className="sandbox-tab-item"
            title="Próximamente: Laboratorio de experimentación SEO"
          >
            <Search size={15} />
            <span>SEO Lab</span>
            <span className="tab-chip">Próximo</span>
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={false}
            disabled
            className="sandbox-tab-item"
            title="Próximamente: Laboratorio de experimentación Paid Media"
          >
            <TrendingUp size={15} />
            <span>Paid Media Lab</span>
            <span className="tab-chip">Próximo</span>
          </button>
        </div>

        {/* Selector de modo de visualización tripartito */}
        <div className="sandbox-view-pill-group" role="group" aria-label="Modo de visualización Sandbox">
          <button
            type="button"
            className={`sandbox-view-pill ${viewMode === "sandbox" ? "is-active" : ""}`}
            onClick={() => setViewMode("sandbox")}
            title="Visualiza exclusivamente el componente sandbox editable"
          >
            <Sparkles size={14} />
            <span>Sandbox</span>
          </button>

          <button
            type="button"
            className={`sandbox-view-pill ${viewMode === "compare" ? "is-active" : ""}`}
            onClick={() => setViewMode("compare")}
            title="Comparativa lado a lado entre la versión Sandbox y la versión Oficial de producción"
          >
            <Columns size={14} />
            <span>Comparativa Lado a Lado</span>
          </button>

          <button
            type="button"
            className={`sandbox-view-pill ${viewMode === "official" ? "is-active" : ""}`}
            onClick={() => setViewMode("official")}
            title="Visualiza exclusivamente el componente oficial de producción como referencia"
          >
            <CheckCircle2 size={14} />
            <span>Oficial</span>
          </button>
        </div>
      </div>

      {/* 3. Safe Harbor & MCP Notice Strip */}
      <aside className="sandbox-mcp-strip" aria-label="Información de entorno seguro">
        <div className="sandbox-mcp-strip-left">
          <GitBranch size={14} color="#06b6d4" />
          <span>
            Laboratorio para <strong>{clientName}</strong> · Componente de trabajo:{" "}
            <code>apps/dashboard_wolf_react/src/components/social/sandbox/SocialTabSandbox.tsx</code>
          </span>
        </div>
        <div className="sandbox-mcp-strip-right">
          <ShieldCheck size={14} color="#10b981" />
          <span>Safe Harbor: Los cambios vía MCP no afectan el tab oficial ni tocan producción.</span>
        </div>
      </aside>

      {/* 4. Main Body */}
      <main className="sandbox-main">
        {error && (
          <div
            className="detail-notice is-danger"
            style={{ marginBottom: "20px", padding: "12px 16px", borderRadius: "8px" }}
          >
            {error}
          </div>
        )}

        {dashboardLoading && !dashboardData && <LoadingState />}
        {loading && !socialData && <LoadingState />}

        {socialData && (
          <>
            {viewMode === "sandbox" && (
              <section className="sandbox-single-container" aria-label="Sandbox Social">
                <SocialTabSandbox
                  data={socialData}
                  clientSlug={effectiveClient}
                  onAuditGenerated={handleAuditGenerated}
                  portalControls={false}
                />
              </section>
            )}

            {viewMode === "official" && (
              <section className="sandbox-single-container" aria-label="Social Oficial de Referencia">
                <SocialTab
                  data={socialData}
                  clientSlug={effectiveClient}
                  onAuditGenerated={handleAuditGenerated}
                  portalControls={false}
                />
              </section>
            )}

            {viewMode === "compare" && (
              <div className="sandbox-compare-container">
                {/* Columna Izquierda: Sandbox */}
                <div className="sandbox-column is-sandbox">
                  <header className="sandbox-column-header">
                    <div className="sandbox-column-title">
                      <Sparkles size={16} />
                      <span>Versión Sandbox (Iteración MCP)</span>
                    </div>
                    <span className="sandbox-column-file-tag">SocialTabSandbox.tsx</span>
                  </header>
                  <div className="sandbox-column-body">
                    <SocialTabSandbox
                      data={socialData}
                      clientSlug={effectiveClient}
                      onAuditGenerated={handleAuditGenerated}
                      portalControls={false}
                    />
                  </div>
                </div>

                {/* Columna Derecha: Oficial */}
                <div className="sandbox-column is-official">
                  <header className="sandbox-column-header">
                    <div className="sandbox-column-title">
                      <CheckCircle2 size={16} />
                      <span>Versión Oficial (Producción)</span>
                    </div>
                    <span className="sandbox-column-file-tag">SocialTab.tsx</span>
                  </header>
                  <div className="sandbox-column-body">
                    <SocialTab
                      data={socialData}
                      clientSlug={effectiveClient}
                      onAuditGenerated={handleAuditGenerated}
                      portalControls={false}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
